import { useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Info, FileText, Calendar, User, Hash, Type } from 'lucide-react';

export default function DocumentInfoModal({ isOpen, onClose, document, space }) {
  // Calculate word and character counts
  const stats = useMemo(() => {
    if (!document?.content) return { wordCount: 0, charCount: 0 };
    
    let text = '';
    try {
      const data = JSON.parse(document.content);
      const blocks = data.blocks || [];
      blocks.forEach(block => {
        if (block.data?.text) text += ' ' + block.data.text;
        if (block.data?.items) {
          block.data.items.forEach(item => {
            if (typeof item === 'string') text += ' ' + item;
            else if (item.content) text += ' ' + item.content;
            else if (item.text) text += ' ' + item.text;
          });
        }
        if (block.data?.content) text += ' ' + block.data.content;
        if (block.data?.code) text += ' ' + block.data.code;
      });
    } catch {
      text = document.content || '';
    }
    
    // Strip HTML tags
    text = text.replace(/<[^>]+>/g, ' ');
    
    const words = text.trim().split(/\s+/).filter(w => w.length > 0);
    return {
      wordCount: words.length,
      charCount: text.replace(/\s/g, '').length
    };
  }, [document?.content]);

  if (!document) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="document-info-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
              <Info className="w-5 h-5 text-slate-600 dark:text-slate-400" />
            </div>
            <span>Document info</span>
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-6 mt-4">
          {/* Title */}
          <div>
            <p className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-1">Title</p>
            <p className="text-slate-900 dark:text-white font-medium">{document.title || 'Untitled'}</p>
          </div>
          
          {/* URL Slug */}
          <div>
            <p className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-1">URL Slug</p>
            <p className="text-slate-900 dark:text-white font-mono text-sm truncate">{document.id}</p>
          </div>
          
          {/* Document ID */}
          <div>
            <p className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-1">Document ID</p>
            <p className="text-slate-900 dark:text-white font-mono text-sm truncate">{document.id}</p>
          </div>
          
          {/* Doc Space ID */}
          <div>
            <p className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-1">Doc Space ID</p>
            <p className="text-slate-900 dark:text-white font-mono text-sm truncate">{document.space_id || space?.id || '-'}</p>
          </div>
          
          {/* Word Count */}
          <div>
            <p className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-1">Word count</p>
            <p className="text-slate-900 dark:text-white text-2xl font-semibold">{stats.wordCount.toLocaleString()}</p>
          </div>
          
          {/* Character Count */}
          <div>
            <p className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-1">Character count</p>
            <p className="text-slate-900 dark:text-white text-2xl font-semibold">{stats.charCount.toLocaleString()}</p>
          </div>
          
          {/* Creation Date */}
          <div>
            <p className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-1">Creation date</p>
            <p className="text-slate-900 dark:text-white">
              {document.created_at ? new Date(document.created_at).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              }) : '-'}
            </p>
          </div>
          
          {/* Created By */}
          <div>
            <p className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-1">Created by</p>
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                <User className="w-3 h-3 text-blue-600 dark:text-blue-400" />
              </div>
              <p className="text-slate-900 dark:text-white">{document.created_by_name || 'Unknown'}</p>
            </div>
          </div>
          
          {/* Last Modified */}
          <div>
            <p className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-1">Last modified</p>
            <p className="text-slate-900 dark:text-white">
              {document.updated_at ? new Date(document.updated_at).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              }) : '-'}
            </p>
          </div>
          
          {/* Last Modified By */}
          <div>
            <p className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-1">Last modified by</p>
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                <User className="w-3 h-3 text-blue-600 dark:text-blue-400" />
              </div>
              <p className="text-slate-900 dark:text-white">{document.updated_by_name || document.created_by_name || 'Unknown'}</p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
